import {
  ExportDataController,
  ExportNumberFormat,
  ExportFileFormat,
  VisualElementController,
  ExportDataEvent,
} from '@sas-dvr/ltjs-bird';

/**
 * @public
 */
export interface ExportDataOptions {
  columns?: string[];
  startRow?: number;
  endRow?: number;
  formattedData?: boolean;
  detailedData?: boolean;
}

/**
 * @public
 */
export type FormatType = 'XLSX' | 'CSV' | 'TSV';

const formatTypes: FormatType[] = ['XLSX', 'CSV', 'TSV'];

const applyOptions = (
  exportController: ExportDataController,
  format: FormatType,
  options?: ExportDataOptions
) => {
  exportController.setFileFormat(ExportFileFormat[format]);

  if (!options) {
    return exportController;
  }

  if (options.startRow && options.startRow >= 0) exportController.setStartRow(options.startRow);
  if (options.endRow && options.endRow >= -1) exportController.setEndRow(options.endRow);
  exportController.setNumberFormat(
    options.formattedData === false ? ExportNumberFormat.RAW : ExportNumberFormat.FORMATTED
  );

  if (options.columns) {
    const availableVariables = exportController.getAvailableVariables();
    const selectedVariables = [];
    for (const column of options.columns) {
      const variable =
        availableVariables?.find((element) => element?.getLabel() === column) ??
        availableVariables?.find((element) => element?.getName() === column);
      if (variable) {
        selectedVariables.push(variable);
      }
    }
    exportController.setSelectedVariables(selectedVariables);
  }

  return exportController;
};

const getExportController = (
  retainedExportController: ExportDataController,
  retainedExportDetailController?: ExportDataController
) => {
  return new Promise<ExportDataController>((resolve, reject) => {
    retainedExportController.attachEventOnce(ExportDataController.EXPORT_READY, () => {
      if (retainedExportDetailController) {
        retainedExportDetailController.attachEventOnce(ExportDataController.EXPORT_READY, () => {
          resolve(retainedExportDetailController);
        });
        retainedExportDetailController.attachEventOnce(
          ExportDataController.EXPORT_WARNING,
          (event) => {
            if (event instanceof ExportDataEvent) {
              retainedExportController?.release();
              retainedExportDetailController?.release();
              return reject(event.getWarnings()?.[0]?.getMessage());
            }
          }
        );
        retainedExportDetailController.prepare();
      } else {
        resolve(retainedExportController);
      }
    });
    retainedExportController.attachEventOnce(ExportDataController.EXPORT_WARNING, (event) => {
      if (event instanceof ExportDataEvent) {
        retainedExportController?.release();
        retainedExportDetailController?.release();
        return reject(event.getWarnings()?.[0]?.getMessage());
      }
    });
    retainedExportController.prepare();
  });
};

const retainExportControllers = (
  controller: VisualElementController,
  detailedData?: boolean
): [ExportDataController, ExportDataController | undefined] => {
  const dataController = controller.getExportDataController();
  if (!dataController) {
    throw new Error('Export controller not found');
  }
  let detailController;
  if (detailedData) {
    detailController = dataController.getExportDetailController();
  }

  return [dataController.retain(), detailController?.retain()];
};

export const getExportDataInfo = (
  visualElementController: VisualElementController,
  detailedData?: boolean
) => {
  const [retainedExportController, retainedExportDetailController] = retainExportControllers(
    visualElementController,
    detailedData
  );
  const controller = detailedData ? retainedExportDetailController : retainedExportController;
  if (!controller) {
    retainedExportController?.release();
    retainedExportDetailController?.release();
    throw new Error('Export controller failed to prepare');
  }

  const availableColumns: { name: string; label: string }[] = [];
  for (const variable of controller.getAvailableVariables() || []) {
    if (variable)
      availableColumns.push({ name: variable.getName() || '', label: variable.getLabel() || '' });
  }

  retainedExportController?.release();
  retainedExportDetailController?.release();
  return {
    rowCount: controller.getEndRow(),
    columnInfo: availableColumns,
  };
};

export const canExportData = (controller: VisualElementController) => {
  const dataController = controller.getExportDataController();
  if (!dataController) {
    return false;
  }
  return true;
};

export const exportData = async (
  controller: VisualElementController,
  format: FormatType,
  options?: ExportDataOptions
): Promise<string> => {
  if (!formatTypes.includes(format)) {
    throw new Error(`Expected a format of type "XLSX", "CSV", or "TSV". Received: ${format}`);
  }

  const [retainedExportController, retainedExportDetailController] = retainExportControllers(
    controller,
    options?.detailedData || false
  );

  const initialExportController = await getExportController(
    retainedExportController,
    retainedExportDetailController
  );
  const exportController = applyOptions(initialExportController, format, options);

  return new Promise((resolve, reject) => {
    exportController.attachEventOnce(ExportDataController.EXPORT_COMPLETED, (event) => {
      retainedExportController?.release();
      retainedExportDetailController?.release();
      if (event instanceof ExportDataEvent) {
        const url = event.getExportURL()?.getFinishedURLString();
        if (!url) {
          return reject('Failed to retrieve export URL');
        } else {
          return resolve(url);
        }
      } else {
        return reject('Failed to complete export');
      }
    });

    exportController.attachEventOnce(ExportDataController.EXPORT_WARNING, (event) => {
      if (event instanceof ExportDataEvent) {
        retainedExportController?.release();
        retainedExportDetailController?.release();
        return reject(event.getWarnings()?.[0]?.getMessage());
      }
    });

    exportController.submit();
  });
};
